/**
 * Lightweight auth helpers.
 *
 * Tokens are stored in localStorage under bm_auth_token.
 * All API calls use relative paths — Apache proxies /api/ → backend.
 *
 * All functions fail gracefully: if the backend is unreachable,
 * the app falls back to its existing localStorage-only behaviour.
 */

const TOKEN_KEY = 'bm_auth_token';
const USER_KEY  = 'bm_auth_user';

export const getToken      = ()  => localStorage.getItem(TOKEN_KEY);
export const setToken      = (t) => localStorage.setItem(TOKEN_KEY, t);
export const clearToken    = ()  => {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
};
export const getStoredUser = ()  => {
  try { return JSON.parse(localStorage.getItem(USER_KEY)); } catch { return null; }
};
export const setStoredUser = (u) =>
  localStorage.setItem(USER_KEY, JSON.stringify(u));

function authHeaders() {
  const token = getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/**
 * Fetch the current user from /api/me.
 * Returns the user object or null on any failure.
 */
export async function fetchMe() {
  if (!getToken()) return null;
  try {
    const res = await fetch('/api/me', { headers: authHeaders() });
    if (res.status === 401 || res.status === 403) return false; // token rejected
    if (!res.ok) return null; // server/network error — preserve state
    return await res.json();
  } catch { return null; } // offline — preserve state
}

/**
 * POST onboarding data to /api/onboarding.
 * On success, stores the returned token + user.
 * Returns the response body or null on any failure.
 */
export async function saveOnboarding(data) {
  try {
    const res = await fetch('/api/onboarding', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify(data),
    });
    if (!res.ok) return null;
    const body = await res.json();
    if (body.token) { setToken(body.token); }
    return body;
  } catch { return null; }
}

export async function loginUser(email, password) {
  try {
    const res = await fetch('/api/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify({ email, password }),
    });
    if (!res.ok) return null;
    const body = await res.json();
    if (body.token) { setToken(body.token); setStoredUser(body); }
    return body;
  } catch { return null; }
}
